<template>
  <b-container
    fluid="md"
    class="square-payment-failure"
  >
    <b-row>
      <b-col class="payment-failure-header">
        <div class="title">
          <font-awesome-icon
            icon="circle-xmark"
            class="icon failure-icon"
            size="2x"
            fixed-width
            color="red"
          />
          <span class="h3 pr-4">
            Payment failed/declined
          </span>
        </div>
        <div class="error-message">
          {{ errorMessage }}
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <div class="transaction-details">
          <h3>Transaction details</h3>
          <div>
            <span class="label">Date:</span>
            <span class="value">{{ txndate | longdate }}</span>
          </div>
          <div>
            <span class="label">Transaction ID </span>
            <span class="value">{{ txnid }}</span>
          </div>
          <div>
            <span class="label">Amount:</span>
            <span class="value">{{ txnAmount | formatCurrency(currency) }}</span>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <div class="payment-details">
          <h3>Payment details</h3>
          <div>
            <span class="label">Payment Reference</span>
            <span
              class="value"
            >{{ reference }}</span>
          </div>
          <div>
            <span class="label">Payment Amount</span>
            <span
              class="value"
            >{{ paymentAmount | formatCurrency }}</span>
          </div>
          <div v-if="isTerminalPayment">
            <span class="label">Status</span>
            <span class="value">{{ paymentStatusDisplay }}</span>
          </div>
          <div v-if="isTerminalPayment && cancelReason">
            <span class="label">Cancel Reason</span>
            <span class="value">{{ cancelReason }}</span>
          </div>
          <div>
            <span class="label">Payment Type</span>
            <span class="value">{{ paymentType }}</span>
          </div>
        </div>
        <div v-if="errorCode">
          <span class="label">Error Code</span>
          <span
            class="value"
          >{{ errorCode }}</span>
        </div>

        <div v-if="errorDetails">
          <span class="label">Error Additional Details</span>
          <span
            class="value"
          >{{ errorDetails }}</span>
        </div>

        <div>
          <div>
            <span class="label">Cetagory</span>
            <span class="value">{{ errorCategory }}</span>
          </div>
        </div>
      </b-col>
    </b-row>
    <square-response-debug-wrapper
      v-if="debug"
      :debug-data="debugData"
    />
  </b-container>
</template>

<script>
import DebugWrapper from './debug.vue'
import CommonTxnFields from './common-txn-fields'

export default {
  name: 'square-payment-response-failure',
  components: {
    'square-response-debug-wrapper': DebugWrapper
  },
  mixins: [
    CommonTxnFields
  ],
  props: {
    debug: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    errorCategory: function() {
      try {
        if (this.isTerminalPayment) {
          if (this.isTerminalTimeoutFailure) {
            return 'Terminal Timeout'
          }
          else if (this.isTerminalPaymentCancelled) {
            return 'Terminal Payment Cancelled'
          }
          else {
            return 'Terminal Payment Error'
          }
        }
        else {
          return this.paymentResponse.error.category
        }
      }
      catch (_err) {
        return ''
      }
    },
    reference: function() {
      try {
        return this.paymentResponse.payment.id
      }
      catch (_err) {
        return ''
      }
    },
    errorCode: function() {
      try {
        return this.paymentResponse.error.code
      }
      catch (_err) {
        return ''
      }
    },
    errorDetails: function() {
      try {
        return this.paymentResponse.error.detail
      }
      catch (_err) {
        return ''
      }
    },
    errorMessage: function() {
      // Custom message for terminal timeout failures
      if (this.isTerminalTimeoutFailure) {
        return 'The payment request timed out. This may be due to a card decline, card not presented, or network request error. Please refer to the Square terminal or contact Square support for more information.'
      }
      try {
        return this.paymentResponse.error.message
      }
      catch (_err) {
        return ''
      }
    },
    isTerminalPayment: function() {
      try {
        return this.paymentResponse.payment.payment_type === 'CARD_PRESENT'
      }
      catch (_err) {
        return false
      }
    },
    isTerminalPaymentCancelled: function() {
      return this.isTerminalPayment &&
        this.paymentStatus === 'CANCELED' &&
        this.cancelReason !== 'TIMED_OUT'
    },
    isTerminalTimeoutFailure: function() {
      return this.isTerminalPayment &&
        this.paymentStatus === 'CANCELED' &&
        this.cancelReason === 'TIMED_OUT'
    },
    paymentStatusDisplay: function() {
      try {
        if (this.isTerminalTimeoutFailure) {
          return 'Terminal Timed Out'
        }
        else {
          return this.paymentResponse.payment.status
        }
      }
      catch (_err) {
        return ''
      }
    },
    paymentStatus: function() {
      try {
        if (this.isTerminalTimeoutFailure) {
          return 'Terminal Timed Out'
        }
        else {
          return this.paymentResponse.payment.status
        }
      }
      catch (_err) {
        return ''
      }
    },
    cancelReason: function() {
      try {
        // Check if it's a terminal payment and has terminal_details
        if (this.isTerminalPayment && this.paymentResponse.payment) {
          return this.paymentResponse.payment.cancel_reason
        }
        return ''
      }
      catch (_err) {
        return ''
      }
    },
    paymentType: function() {
      try {
        // Check if it's a terminal payment and has terminal_details
        const paymentType = this.paymentResponse.payment.payment_type
        // Convert to more readable format
        switch (paymentType) {
        case 'CARD_PRESENT':
          return 'Card Present'
        case 'CARD_NOT_PRESENT':
          return 'Card Not Present'
        default:
          return 'Other'
        }
      }
      catch (_err) {
        return ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .square-payment-failure {
    .payment-failure-header {
      .title {
        font-size: 2.2rem;
        margin-bottom: 1rem;
        display: flex;
        align-items: center;
      }
      .message {
        font-size: 1rem;
        margin-bottom: 1rem;
      }
    }
    .label {
      display: inline-block;
      width: 200px;
    }
    .success-icon {
      color: green;
    }
  }
</style>
